import { useEffect, useState, useCallback } from "react";
import { LoadMoreButton } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";

import MaintenanceTable from "./components/maintenanceTable";
import SkeletonTable from "./components/skeletonTable";
import EmptyTable from "./components/emptyTable";
import BigNumbers from "../../../components/bigNumbers";

import useMaintenance from "../../../hooks/useMainteinance";
import useAssetsConnectionStatus from "../../../hooks/useAssetsConnectionStatus";
import useUptimeAssets from "../../../hooks/useUptimeAsset";
import useAssets from "../../../hooks/useAssets";
import useAssetsPlans from "../../../hooks/useAssetsPlans";
import useAutoLoadMore from "../../../hooks/useAutoLoadMore";

import { Asset } from "../../../models/asset";

const MonitoringContent = (props: any) => {
  const intl = useIntl();
  const { allAssets } = useAssets();
  const {
    allSummaryAssets,
    assetIds,
    loading,
    loadMoreLoading,
    loadAssetsPaged,
    assets,
    getMoreAssets,
    page,
    assetIdsPaged,
    disableBigNumber,
  } = useMaintenance();
  const { uptimeAssets, getUptimeAssets, refreshUptimeAsset } = useUptimeAssets();
  const { assetsPlans, getAssetsPlans } = useAssetsPlans();
  const { assetsConnectionStatus, getAssetsConnectionStatus } =
    useAssetsConnectionStatus();

  const [availableToScheduleAssetIds, setAvailableToScheduleAssetIds] =
    useState<string[]>([]);

  const handleAssetUpdate = useCallback((assetId: string) => {
    refreshUptimeAsset(assetId);
  }, [refreshUptimeAsset]);

  useEffect(() => {
    loadAssetsPaged(undefined, undefined, allAssets);
  }, []);

  useEffect(() => {
    if (props.assetIds?.length) {
      loadAssetsPaged(props.assetIds, true, allAssets);
    } else if (props.isSidebarOpen && Array.isArray(props.assetIds)) {
      loadAssetsPaged([], true, allAssets);
    }
  }, [props.assetIds, props.isSidebarOpen]);

  useEffect(() => {
    getUptimeAssets(
      assetIdsPaged[page]?.map((asset: any) => asset.id)
    );
    getAssetsConnectionStatus(
      assetIdsPaged[page]?.map((asset: any) => asset.id)
    );
  }, [assetIdsPaged, page]);

  useEffect(() => {
    getAssetsPlans(
      assets?.map((asset: Asset) => asset.accountAssetId ?? asset.id)
    );
  }, [assets]);

  return (
    <>
      <BigNumbers
        assetIds={assetIds}
        setAssetIds={(assetIds: string[] | undefined) =>
          loadAssetsPaged(assetIds, undefined, allAssets)
        }
        assets={allSummaryAssets}
        disabled={disableBigNumber}
        bigTableLoading={loading}
        allAssets={allAssets}
        persona={props.persona}
        setAvailableToScheduleAssetIds={setAvailableToScheduleAssetIds}
        isSidebarOpen={props.isSidebarOpen}
      />
      <div style={{ marginTop: 30 }}>
        {loading ? (
          <SkeletonTable />
        ) : (
          <>
            <MaintenanceTable
              assets={assets}
              uptimeAssets={uptimeAssets}
              assetsConnectionStatus={assetsConnectionStatus}
              assetsPlans={assetsPlans}
              availableToScheduleAssetIds={availableToScheduleAssetIds}
              allSummaryAssets={allSummaryAssets}
              isSidebarOpen={props.isSidebarOpen}
              onAssetUpdate={handleAssetUpdate}
            />
            {assets.length > 0 &&
              (loadMoreLoading ? (
                <SkeletonTable onlyRows />
              ) : (
                <LoadMoreButton
                  loaded={assets.length}
                  total={allSummaryAssets.length}
                  onLoadMore={getMoreAssets}
                  loadMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.loadMore" })}
                  noMoreMessage={intl.formatMessage({ id: "intl-msg:monitoring.allLoaded" })}
                />
              ))}

            {assets.length === 0 && <EmptyTable />}
          </>
        )}
      </div>
    </>
  );
};

const Monitoring = (props: any) => {
  return <MonitoringContent {...props} />;
};

export default Monitoring;
